import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useUnsavedChangesGuard } from "../contexts/UnsavedChangesContext";

import { Drawer, List, ListItemIcon, ListItemText, Box, Divider, ListSubheader, Avatar, Card, CardHeader, Skeleton, Chip, ListItemButton, Collapse, IconButton, Typography, Menu, MenuItem, Tooltip } from "@mui/material";
import { SlowMotionVideoOutlined as SlowMotionVideoOutlinedIcon, CorporateFare as CorporateFareIcon, ReorderOutlined, PeopleAltOutlined, LogoutOutlined, ExpandLess, ExpandMore, Settings as SettingsIcon, HourglassEmpty as HourglassEmptyIcon } from "@mui/icons-material";

import { AuthContext } from "../contexts/AuthContext";
import { useSession } from '../contexts/SessionContext';
import { useActivity, ACTIVITY_TYPES, RESOURCE_TYPES } from "../contexts/ActivityTrackingContext";
import apiService from "../services/api.service";
import { addEncodedSessionToUrl, getSessionStartTime, getAccessToken, getRefreshToken, getDeviceInfo } from "../utils/urlEncoder";

import OpusLogo from "../style/img/opus-logo.webp";
import packageJson from '../../package.json'

import TimelineIcon from '@mui/icons-material/Timeline';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { Icon as OpusIcon, UserAvatar as OpusUserAvatar } from "./common/opus";
import ProfileModal from "./profile/ProfileModal";
import { useAlert } from "../contexts/AlertContext";
import { usePermissions } from "../contexts/PermissionsContext";
import AetherLogo from '../style/img/Aether.png'
const drawerWidth = 260;

export default function LeftDrawer() {
  const location = useLocation();
  const { isDrawerOpen, selectedOrgId, updateSelectedOrgId, organizations, setOrganizations } = useSession();
  const [orgMenuAnchor, setOrgMenuAnchor] = useState(null);
  // If the currently mounted page (e.g. scene edit) has registered unsaved
  // changes, route nav clicks through its own leave-confirmation instead of
  // navigating straight away.
  const unsavedGuard = useUnsavedChangesGuard();
  const guardedNavigate = (path) => {
    if (unsavedGuard?.hasUnsavedChanges) {
      unsavedGuard.requestLeave(() => navigate(path));
    } else {
      navigate(path);
    }
  };

  const { logout, userInfo, updateUserInfo } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);
  const { trackActivity } = useActivity();

  const userRole = userInfo?.data?.role?.name;
  const isSuperAdmin = userRole === "super_admin";
  const { can } = usePermissions(); // OPUS-913: permission-gated nav
  const environment = process.env.REACT_APP_ENV;
  const userOrg = userInfo?.data?.organization_id?._id;
  const selectedOrg = organizations.find((o) => o._id === selectedOrgId);
  const selectedOrgName = selectedOrg?.name || "";

  useEffect(() => {
    if (userInfo && userInfo.data) {
      setIsLoading(false);
    }
  }, [userInfo]);

  // OPUS-1045: the org-scoped CTA fetch + secure launch (handoff) moved into
  // the shared useQuickLinks hook, consumed by the dedicated My Apps page now
  // that the launchers no longer live on the rail.

  // OPUS-913: load the org list once + seed the global selected org. Non-super
  // admins are pinned to their own org; super admins keep their persisted choice
  // when still valid, else fall back to their own org / the first available.
  useEffect(() => {
    let cancelled = false;
    apiService.getOrganizationsFromScene()
      .then((data) => {
        const orgs = Array.isArray(data) ? data : (data?.data || []);
        if (cancelled) return;
        setOrganizations(orgs);
        if (!isSuperAdmin) { if (userOrg) updateSelectedOrgId(userOrg); return; }
        const valid = selectedOrgId && orgs.some((o) => o._id === selectedOrgId);
        if (!valid) {
          const fallback = (userOrg && orgs.some((o) => o._id === userOrg)) ? userOrg : (orgs[0]?._id || "");
          if (fallback) updateSelectedOrgId(fallback);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin, userOrg]);


  const [openLogoutMenu, setOpenLogoutMenu] = useState(false);

  // OPUS-913: account menu + edit-profile modal (design footer).
  const navigate = useNavigate();
  const alert = useAlert();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const userId = userInfo?.data?._id;
  const avatarKey = userId ? `opus-avatar-${userId}` : null;
  const [avatarPref, setAvatarPref] = useState({ type: "initials", color: "#FF455C" });
  useEffect(() => {
    if (!avatarKey) return;
    try { const v = JSON.parse(localStorage.getItem(avatarKey)); if (v && v.type) setAvatarPref(v); } catch (_) { /* ignore */ }
  }, [avatarKey]);

  // OPUS-913: the signed-in user's own renderers (incl. their preview), shown in the profile modal.
  const [myRenderers, setMyRenderers] = useState([]);
  useEffect(() => {
    if (!profileOpen || !userOrg) return undefined;
    let cancelled = false;
    apiService.getRenderersByOrganization(userOrg)
      .then((resp) => {
        const list = resp?.data?.renderers || resp?.renderers || resp?.data || (Array.isArray(resp) ? resp : []);
        const mine = (Array.isArray(list) ? list : []).filter((r) => {
          const oid = r.owner?.user_id || r.owner_user_id;
          const cby = r.created_by?.id || r.created_by;
          return (oid && String(oid) === String(userId)) || (cby && String(cby) === String(userId));
        });
        if (!cancelled) setMyRenderers(mine);
      })
      .catch(() => { if (!cancelled) setMyRenderers([]); });
    return () => { cancelled = true; };
  }, [profileOpen, userOrg, userId]);
  const profileForModal = {
    name: userInfo?.data?.name || userInfo?.data?.user_name || "",
    username: userInfo?.data?.user_name || "",
    email: userInfo?.data?.email || "",
    org: userInfo?.data?.organization_id?.name || "",
    avatar: avatarPref,
  };
  const userName = userInfo?.data?.user_name;
  const handleSaveProfile = async (form) => {
    if (avatarKey && form.avatar) { try { localStorage.setItem(avatarKey, JSON.stringify(form.avatar)); } catch (_) { /* ignore */ } setAvatarPref(form.avatar); }
    // PUT /users/:user_name (keyed by user_name, not _id). Persists name + email.
    if (userName) {
      try { await apiService.updateUser(userName, { name: form.name, email: form.email }); } catch (e) { alert?.error && alert.error(e?.message || "Couldn't save profile"); return; }
    }
    // Reflect immediately in context + persisted userData (no reload needed).
    updateUserInfo && updateUserInfo({ name: form.name, email: form.email });
    alert?.success && alert.success("Profile updated");
  };

  const handleToggle = () => {
    setOpenLogoutMenu(!openLogoutMenu);
  };

  const handleLogout = () => {
    if (userInfo?.data) {
      trackActivity(
        ACTIVITY_TYPES.LOGOUT,
        RESOURCE_TYPES.USER,
        userInfo.data._id,
        userInfo.data.name || userInfo.data.user_name,
        {
          success: true,
          method: 'explicit',
          userAgent: navigator.userAgent
        }
      );
    }
    logout();
  };


  const baseMenu = [
    {
      // OPUS-913: Dashboard home nav item (matches v5 sidebar) — the home now
      // has a real design, so give it a top-level entry above the sections.
      path: "/dashboard",
      perm: "dashboard.access",
      icon: <OpusIcon name="home" size={22} />,
      text: "Dashboard",
    },
    {
      path: "/dashboard/renderer-management",
      perm: "dashboard.renderers.view",
      icon: <OpusIcon name="rendererList" size={22} />,
      text: "Renderer list",
      // OPUS-1045: the scene-controls app lives under its own path but belongs
      // to the renderer flow — keep "Renderer list" lit while operating it.
      match: ["/dashboard/scene-controls"],
      subHeader: <ListSubheader sx={{ background: "none", mt: 2, fontSize: "0.875em" }}>Your graphics</ListSubheader>,
    },
    {
      path: "/dashboard/scene-management",
      perm: "dashboard.scenes.view",
      icon: <OpusIcon name="scene" size={22} />,
      text: "Scene list",
    },
    {
      path: "/dashboard/session-management",
      icon: <HourglassEmptyIcon style={{ fontSize: 22, display: 'block' }} />,
      text: "Session list",
    },
    // Gallery list removed (OPUS-913) — to be replaced by Session list later.
  ];

  const orgAdminMenu = [
    {
      path: "/dashboard/user-management",
      perm: "dashboard.users.view",
      icon: <OpusIcon name="users" size={22} />,
      text: "Users",
      subHeader: <ListSubheader sx={{ background: "none", mt: 2, fontSize: "0.875em" }}>Your account</ListSubheader>,
    },
    // OPUS-1045: the Settings page was just an app editor — that capability
    // now lives in My Apps (gated by the same dashboard.settings.view perm),
    // so the standalone Settings nav entry was removed.
  ]

  const superAdminMenu = [
    {
      path: "/dashboard/organisation",
      perm: "organisations.read",
      icon: <OpusIcon name="org" size={22} />,
      text: "Organisation",
    },
    {
      // Grouped with the team/org-management items, directly under Organisation.
      // Labelled "Roles" (not "Roles & Permissions") so it fits one line and
      // matches the page title + /dashboard/roles route.
      path: "/dashboard/roles",
      perm: "roles.manage",
      icon: <OpusIcon name="shield" size={22} />,
      text: "Roles",
    },
    {
      path: "/dashboard/activity-logs",
      perm: "dashboard.activity.view",
      icon: <OpusIcon name="activity" size={22} />,
      text: "Activity Logs",
    }
    // {
    //   path: "/dashboard/admin-portal",
    //   icon: <img
    //     src={AetherLogo}
    //     alt="Aether"
    //     style={{ width: 24, height: 24 }}
    //   />,
    //   text: "Aether",
    // },
  ];

  // OPUS-1045: My Apps launcher lives at the bottom of the "Your account"
  // section (below Activity Logs), not at the top of the rail. dashboard.access
  // keeps it visible to every dashboard user.
  const accountTrailingMenu = [
    {
      path: "/dashboard/my-apps",
      perm: "dashboard.access",
      icon: <OpusIcon name="layers" size={22} />,
      text: "My Apps",
    },
  ];

  // Filter out scene option for operator role
  // OPUS-913: nav is permission-gated (PermissionsContext). Each item declares the
  // permission that reveals it; super_admin (*) sees all, others see only what
  // their @ae/auth role permissions allow. New roles work with no code change.
  const menu = [...baseMenu, ...orgAdminMenu, ...superAdminMenu, ...accountTrailingMenu].filter(
    (item) => !item.perm || can(item.perm)
  );

  return (
    <Drawer
      anchor="left"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          // Pin the nav rail to a fixed 16px base so any future change to the
          // global base font (theme html %) cannot enlarge the drawer
          // (OPUS-893). All drawer text is em-relative to this base.
          fontSize: "16px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          p: 0,
          pt: 0,
          m: 0,
          // OPUS-913: boxy flush full-height rail with a right border (no floating
          // margin / rounded corners). Translucent sidebar over the gradient body.
          borderRadius: 0,
          height: "100vh",
          borderRight: "1px solid var(--border)",
          background: "var(--bg-sidebar)",
        },
      }}
      variant="persistent"
      open={isDrawerOpen}
    >
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}>
        <Box sx={{ textAlign: "center", mt: 2, mb: 1.5, flexShrink: 0 }}>
          <Link
            to="/dashboard"
            style={{ textDecoration: "none" }}
            onClick={(e) => {
              if (unsavedGuard?.hasUnsavedChanges) {
                e.preventDefault();
                guardedNavigate("/dashboard");
              }
            }}
          >
            <img src={OpusLogo} alt="Logo" style={{ width: "220px", opacity: '100%', cursor: 'pointer' }} />
          </Link>
        </Box>

        {/* OPUS-913: global organisation selector (super admin) — single source */}
        {isSuperAdmin && (
          <Box sx={{ px: "14px", pb: "6px" }}>
            <Box
              component="button"
              onClick={(e) => setOrgMenuAnchor(e.currentTarget)}
              sx={{
                display: "flex", alignItems: "center", gap: "11px", width: "100%",
                p: "9px 11px", borderRadius: "10px", border: "1px solid var(--border)",
                background: orgMenuAnchor ? "var(--surface-2)" : "#00000030", cursor: "pointer",
                textAlign: "left", color: "#fff", "&:hover": { background: "var(--surface-2)" },
              }}
            >
              <Avatar variant="rounded" src={selectedOrg?.logoUrl || undefined} sx={{ width: 28, height: 28, bgcolor: "var(--coral)", color: "#fff", fontSize: 13, fontWeight: 700, borderRadius: "30%" }}>
                {(selectedOrgName || "?").trim().charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 10, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.08em", lineHeight: 1.2 }}>Organisation</Typography>
                <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: "#fff", lineHeight: 1.3 }}>{selectedOrgName || "—"}</Typography>
              </Box>
              <ExpandMore sx={{ color: "var(--text-dim)", fontSize: 18, transform: orgMenuAnchor ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
            </Box>
            <Menu
              anchorEl={orgMenuAnchor}
              open={Boolean(orgMenuAnchor)}
              onClose={() => setOrgMenuAnchor(null)}
              slotProps={{ paper: { sx: { width: 232, maxHeight: 360, mt: 0.5 } } }}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              {organizations.map((o) => {
                const sel = o._id === selectedOrgId;
                return (
                  <MenuItem key={o._id} selected={sel} onClick={() => { updateSelectedOrgId(o._id); setOrgMenuAnchor(null); }} sx={{ gap: 1.25 }}>
                    <Avatar variant="rounded" src={o.logoUrl || undefined} sx={{ width: 24, height: 24, bgcolor: "var(--coral)", color: "#fff", fontSize: 12, fontWeight: 700, borderRadius: "30%" }}>
                      {(o.name || "?").trim().charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography noWrap sx={{ flex: 1, fontSize: 13.5, fontWeight: sel ? 700 : 500 }}>{o.name}</Typography>
                    {sel && <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "var(--green)" }} />}
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>
        )}

        <List sx={{ pt: 0.5, mt: 0 }}>
          {menu.map((item, index) => {
            // OPUS-1045: keep a nav item lit on its nested sub-routes (e.g.
            // "Scene list" while editing a scene, "Renderer list" while in
            // scene-controls), not only on an exact path match. Home is a
            // prefix of every route, so it matches exactly only.
            const onPathOrUnder = (base) =>
              location.pathname === base || location.pathname.startsWith(base + "/");
            const selected =
              item.path === "/dashboard"
                ? location.pathname === "/dashboard"
                : onPathOrUnder(item.path) || (item.match || []).some(onPathOrUnder);
            return (
              <React.Fragment key={index}>
                {item?.subHeader}
                <Tooltip title={item.text} placement="right" arrow>
                <Link
                  to={item.path}
                  style={{ display: "inherit", textDecoration: "none", color: "white" }}
                  onClick={(e) => {
                    if (unsavedGuard?.hasUnsavedChanges) {
                      e.preventDefault();
                      guardedNavigate(item.path);
                    }
                  }}
                >
                  <ListItemButton
                    selected={selected}
                    sx={{
                      mt: 0.5,
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: selected ? "#00000035" : "transparent",
                      borderRadius: "var(--r-md)",
                      p: 0,
                      pl: 2,
                      mx: 0.5,
                      transition: "background .13s",
                      "&:hover": { backgroundColor: selected ? "#00000035" : "#ffffff0a" },
                      "&.Mui-selected": { backgroundColor: "#00000035" },
                      "&.Mui-selected:hover": { backgroundColor: "#00000040" },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        position: "relative",
                        fontSize: "1.3em",
                        minWidth: "unset",
                        mr: 2,
                      }}
                    >
                      {selected && (
                        <Divider
                          orientation={"vertical"}
                          color={"white"}
                          sx={{
                            borderRadius: "6px",
                            backgroundColor: "var(--cyan)",
                            boxShadow: "0 0 10px var(--cyan)",
                            height: "22px",
                            width: "3px",
                            position: "absolute",
                            left: "-0.5em",
                            top: "50%",
                            transform: "translateY(-50%)",
                          }}
                        />
                      )}
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      slotProps={{
                        primary: { noWrap: true, style: { fontSize: "1.3em", fontWeight: selected ? "bold" : "" } },
                      }}
                    />
                  </ListItemButton>
                </Link>
                </Tooltip>
              </React.Fragment>
            );
          })}
        </List>

        {/* OPUS-1045: the "Quick Links" launchers moved off the rail into the
            dedicated /dashboard/my-apps page (see the "My Apps" nav item). */}
      </div>

      <Box sx={{ textAlign: "center", flexShrink: 0 }}>
        <Divider sx={{ mx: 2 }}></Divider>

        {isLoading ? (
          <Card elevation={0} sx={{ textAlign: "left" }}>
            <CardHeader
              avatar={<Skeleton variant="rounded" width={40} height={40} />}
              title={<Skeleton width={120} />}
              subheader={<Skeleton width={80} />}
            />
          </Card>
        ) : (
          <Box sx={{ position: "relative", px: 1.5 }}>
            {/* account menu popover */}
            {accountMenuOpen && (
              <>
                <Box sx={{ position: "fixed", inset: 0, zIndex: 120 }} onClick={() => setAccountMenuOpen(false)} />
                <Box sx={{ position: "absolute", left: 14, right: 14, bottom: "calc(100% + 6px)", zIndex: 130, background: "var(--elevated)", border: "1px solid var(--border-strong)", borderRadius: "var(--r-md)", boxShadow: "var(--shadow-lg)", p: 0.75 }}>
                  {[
                    { icon: "users", label: "Edit profile", fn: () => { setAccountMenuOpen(false); setProfileOpen(true); } },
                    { icon: "info", label: "User guide", fn: () => { setAccountMenuOpen(false); window.dispatchEvent(new Event("opus:open-guide")); } },
                    { icon: "logout", label: "Log out", danger: true, fn: () => { setAccountMenuOpen(false); handleLogout(); } },
                  ].map((m) => (
                    <Box key={m.label} component="button" type="button" onClick={m.fn}
                      sx={{ display: "flex", alignItems: "center", gap: 1.4, width: "100%", p: "9px 11px", border: "none", background: "transparent", borderRadius: "var(--r-sm)", cursor: "pointer", color: m.danger ? "var(--coral)" : "var(--text)", fontSize: 14, fontFamily: "var(--font-sans)", textAlign: "left", "&:hover": { background: "var(--surface-3)" } }}>
                      <OpusIcon name={m.icon} size={16} />{m.label}
                    </Box>
                  ))}
                </Box>
              </>
            )}
            <Box component="button" type="button" onClick={() => setAccountMenuOpen((o) => !o)}
              sx={{ display: "flex", alignItems: "center", gap: 1.25, width: "100%", p: "8px 6px", border: "none", borderRadius: "var(--r-md)", background: accountMenuOpen ? "var(--surface-2)" : "transparent", cursor: "pointer", "&:hover": { background: "var(--surface-2)" } }}>
              <OpusUserAvatar profile={profileForModal} size={34} />
              <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: "#fff", lineHeight: 1.3 }}>{userInfo?.data?.name || userInfo?.data?.user_name || "User"}</Typography>
                <Typography noWrap sx={{ fontSize: 12, color: "var(--text-dim)" }}>@{userInfo?.data?.user_name || "user"} · {userInfo?.data?.organization_id?.name || "ORG"}</Typography>
              </Box>
              <OpusIcon name="chevronDown" size={16} style={{ color: "var(--text-dim)", transform: accountMenuOpen ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
            </Box>
          </Box>
        )}
        <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} profile={profileForModal} renderers={myRenderers} onSave={handleSaveProfile} />

        <Divider sx={{ mx: 2, mb: 2 }}></Divider>

        <Box sx={{ textAlign: 'center', p: 0, m: 0, mb: 1, mt: 1.5, opacity: '33%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} >
          <Chip color="error" size="small" label={environment} sx={{ mr: 1 }} />
          v{packageJson.serviceVersion || packageJson.version}
        </Box>
      </Box>
    </Drawer>
  );
}